import React, { useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import { PIPELINE_STAGES, getStage } from "../../../config/pipelineStages";
import PipelineCard from "./PipelineCard";

/**
 * PipelineKanban — renders one column per pipeline stage.
 *
 * Drag-drop:
 *   - DndContext wraps the entire kanban
 *   - Each column is a useDroppable target keyed on stage.id
 *   - Each card uses useDraggable (inside PipelineCard)
 *   - PointerSensor distance:8 prevents accidental drags from clicks
 *   - TouchSensor delay:250ms enables long-press-to-drag on mobile
 *   - DragOverlay renders a floating preview of the dragged card so it
 *     visually sits above all columns instead of being clipped
 *   - On drag end: if dropped on a different stage, call onUpdateStage
 *
 * Layout:
 *   - Desktop: all columns visible side-by-side via flex row
 *   - Mobile : columns are scroll-snap children, swipe between them.
 */
export default function PipelineKanban({
  leads,
  onUpdateStage,
  onArchive,
  onOpenDetail,
  onUpdateContactDetails,
  onMarkPaid,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    })
  );

  const [activeLead, setActiveLead] = useState(null);

  // Bucket leads by stage. Anything outside the locked stage list (shouldn't
  // happen post-migration) drops to the New column.
  const byStage = {};
  for (const s of PIPELINE_STAGES) byStage[s.id] = [];
  for (const lead of leads || []) {
    const status = lead.status || "New";
    if (byStage[status]) byStage[status].push(lead);
    else byStage["New"].push(lead);
  }

  function handleDragStart(event) {
    const { active } = event;
    const lead = (leads || []).find((l) => l.id === active.id) || null;
    setActiveLead(lead);
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    setActiveLead(null);
    if (!over || !active) return;
    const targetStage = over.id;
    const sourceStage = active.data?.current?.stage;
    if (!targetStage || sourceStage === targetStage) return;
    onUpdateStage?.(active.id, targetStage);
  }

  function handleDragCancel() {
    setActiveLead(null);
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div
        className="rqp-pipeline-kanban"
        style={{
          display: "flex",
          gap: "12px",
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          paddingBottom: "8px",
        }}
      >
        {PIPELINE_STAGES.map((stage) => (
          <DroppableColumn
            key={stage.id}
            stage={stage}
            items={byStage[stage.id] || []}
            onUpdateStage={onUpdateStage}
            onArchive={onArchive}
            onOpenDetail={onOpenDetail}
            onUpdateContactDetails={onUpdateContactDetails}
            onMarkPaid={onMarkPaid}
          />
        ))}
      </div>

      {/* Floating clone of the dragged card — sits above columns */}
      <DragOverlay dropAnimation={{ duration: 180, easing: "ease" }}>
        {activeLead ? <DragPreviewCard lead={activeLead} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

function DroppableColumn({
  stage,
  items,
  onUpdateStage,
  onArchive,
  onOpenDetail,
  onUpdateContactDetails,
  onMarkPaid,
}) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });

  return (
    <div
      ref={setNodeRef}
      className="rqp-pipeline-column"
      data-stage-id={stage.id}
      style={{
        flex: "0 0 auto",
        width: "min(78vw, 250px)",
        scrollSnapAlign: "start",
        background: stage.bg,
        border: isOver
          ? `2px dashed ${stage.color}`
          : `1px solid ${stage.color}33`,
        borderRadius: "12px",
        padding: "10px",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        maxHeight: "calc(100vh - 220px)",
        minHeight: "200px",
        transition: "border 120ms",
      }}
    >
      {/* Column header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          paddingBottom: "8px",
          borderBottom: `1px solid ${stage.color}33`,
          marginBottom: "4px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            minWidth: 0,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: stage.color,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: "0.78rem",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: stage.color,
            }}
          >
            {stage.label}
          </span>
        </div>
        <span
          style={{
            fontSize: "0.72rem",
            fontWeight: 700,
            color: stage.color,
            background: "var(--surface, #fff)",
            border: `1px solid ${stage.color}33`,
            borderRadius: "999px",
            padding: "1px 7px",
            minWidth: "20px",
            textAlign: "center",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {items.length}
        </span>
      </div>

      {/* Cards (scrollable within column) */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          margin: "0 -4px",
          padding: "0 4px",
        }}
      >
        {items.length === 0 ? (
          <div
            className="muted small"
            style={{
              padding: "24px 8px",
              textAlign: "center",
              fontSize: "0.78rem",
              fontStyle: "italic",
              color: "var(--text-faint, #a8897c)",
            }}
          >
            Nothing here
          </div>
        ) : (
          items.map((lead) => (
            <PipelineCard
              key={lead.id}
              lead={lead}
              onUpdateStage={onUpdateStage}
              onArchive={onArchive}
              onOpenDetail={onOpenDetail}
              onUpdateContactDetails={onUpdateContactDetails}
              onMarkPaid={onMarkPaid}
            />
          ))
        )}
      </div>
    </div>
  );
}

// Lightweight read-only preview rendered inside DragOverlay. We avoid
// rendering the real PipelineCard here because it would re-fire useDraggable
// hooks. Mirror just enough styling to show a recognizable card floating
// above the columns while the user drags.
function DragPreviewCard({ lead }) {
  const status = lead?.status || "New";
  const stage = getStage(status) || PIPELINE_STAGES[0];
  const customer =
    lead?.customer ||
    lead?.form?.fullName ||
    (lead?.detailsLoaded ? "Unnamed customer" : "Loading…");

  return (
    <div
      style={{
        background: "var(--surface, #fff)",
        border: "1px solid var(--border, rgba(140,100,80,0.14))",
        borderLeft: `3px solid ${stage.color}`,
        borderRadius: "10px",
        padding: "10px 12px",
        boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
        width: "min(78vw, 250px)",
        cursor: "grabbing",
        transform: "rotate(-1.5deg)",
      }}
    >
      <div style={{ fontWeight: 600, fontSize: "0.92rem", color: "var(--text)" }}>
        {customer}
      </div>
      {lead?.trade && (
        <div
          style={{
            fontSize: "0.72rem",
            color: "var(--text-soft, #6b5448)",
            marginTop: "2px",
          }}
        >
          {lead.trade}
        </div>
      )}
    </div>
  );
}
